// src/components/Navbar.tsx

import React, { useState, MouseEvent, useContext } from 'react';
import {
    AppBar,
    Toolbar,
    Typography,
    Button,
    IconButton,
    Menu,
    MenuItem,
    useMediaQuery,
    useTheme,
    Box,
    Tooltip,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';

const Navbar: React.FC = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const navigate = useNavigate();
    const location = useLocation();

    // Pull `email` and `role` from AuthContext
    const { logout, email, role, isLoggedIn } = useContext(AuthContext);

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const handleMenuOpen = (event: MouseEvent<HTMLElement>) =>
        setAnchorEl(event.currentTarget);
    const handleMenuClose = () => setAnchorEl(null);
    const handleLogout = () => {
        logout();
        navigate('/login');
        handleMenuClose();
    };

    // Build mobile menu items. We mark “Create Record” disabled if role==="viewer".
    const mobileMenuItems = isLoggedIn()
        ? [
              { label: 'Dashboard', to: '/dashboard' },
              {
                  label: 'Create Record',
                  to: '/record-editor',
                  disabled: role === 'viewer',
              },
              { label: 'Logout', action: handleLogout },
          ]
        : [{ label: 'Login', to: '/login' }];

    return (
        <AppBar position="static" color="primary">
            <Toolbar>
                {/* Inline SVG logo on the left */}
                <Box
                    component={RouterLink}
                    to="/"
                    sx={{ mr: 2, display: 'flex', alignItems: 'center' }}
                >
                    {/* … SVG contents omitted for brevity … */}

                    <svg
                        width="50"
                        height="40"
                        viewBox="0 0 50 40"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M30.324 11.5361C29.9803 11.5361 29.6576 11.6882 29.4597 11.9505L28.1978 13.6165C27.9458 13.9518 27.9458 14.3912 28.1978 14.7264L35.2198 24.0975C36.3672 25.6322 38.8058 25.6864 40.0386 24.2078L49.2701 13.1042C49.8032 12.4649 49.3076 11.5403 48.435 11.5403H30.3302L30.326 11.5361H30.324Z"
                            fill="white"
                        />
                        <path
                            d="M19.1559 11.519C19.4996 11.519 19.8223 11.6711 20.0202 11.9335L21.2842 13.5994C21.5362 13.9347 21.5362 14.3741 21.2842 14.7094L14.2164 24.1345C13.0856 25.6381 10.6929 25.6943 9.48505 24.2407L0.22022 13.083C-0.312888 12.4437 0.182736 11.519 1.05528 11.519H19.1601H19.1559Z"
                            fill="white"
                        />
                        <path
                            d="M36.3519 28.106C36.633 27.7708 36.6414 27.3085 36.3831 26.9607L28.1387 15.9674C27.7201 15.4114 26.8289 15.4073 26.4124 15.9633L25.3753 17.3273C25.2504 17.4918 25.1816 17.6875 25.1816 17.8875V38.6787C25.1816 39.6074 26.4582 40.0052 27.0683 39.2721L36.3457 28.1123L36.3498 28.104L36.3519 28.106Z"
                            fill="white"
                        />
                        <path
                            d="M13.2211 28.106C12.9399 27.7708 12.9316 27.3085 13.1898 26.9607L21.4343 15.9674C21.8528 15.4114 22.7441 15.4073 23.1606 15.9633L24.1977 17.3273C24.3226 17.4918 24.3913 17.6875 24.3913 17.8875V38.6787C24.3913 39.6074 23.1148 40.0052 22.5026 39.2721L13.2252 28.1123L13.2211 28.104V28.106Z"
                            fill="white"
                        />
                        <path
                            d="M16.0139 6.54619C16.7531 6.63573 17.4237 6.72944 18.0338 6.83356C18.6398 6.9356 19.1875 7.05014 19.6769 7.17717C20.1684 7.3042 20.6057 7.44789 20.9909 7.61448C21.3824 7.779 21.7219 7.96642 22.028 8.17883C22.332 8.39124 22.5965 8.62864 22.836 8.89936C23.0692 9.17007 23.2775 9.47203 23.4649 9.81563C23.6481 10.1551 23.8147 10.5362 23.9605 10.961C24.1084 11.3837 24.2458 11.8502 24.3749 12.3687C24.4478 12.6561 25.0455 12.6561 25.1142 12.3687C25.1704 12.1521 25.2245 11.9501 25.2787 11.7502C25.4349 11.2025 25.5994 10.7153 25.7931 10.2842C25.9951 9.82605 26.2241 9.43246 26.5011 9.08469C26.7718 8.73692 27.0883 8.44122 27.4569 8.18091C27.7589 7.9685 28.1046 7.78316 28.494 7.61657C28.8834 7.44997 29.3207 7.30628 29.808 7.17925C30.2995 7.05222 30.8409 6.93769 31.4511 6.83565C32.0571 6.73361 32.7276 6.63573 33.4711 6.54827C33.8896 6.49829 33.8896 6.0818 33.4711 6.03182C33.1629 5.99434 32.8651 5.95477 32.5798 5.91729C31.7947 5.80692 31.0971 5.69238 30.4765 5.55702C29.8205 5.4175 29.2499 5.25507 28.7543 5.06556C28.2545 4.87398 27.8255 4.65948 27.459 4.40126C27.155 4.18885 26.8905 3.95145 26.651 3.68073C26.4178 3.41001 26.2095 3.10806 26.0221 2.76445C25.8389 2.42501 25.6723 2.04393 25.5265 1.61911C25.3786 1.19637 25.2412 0.729899 25.1121 0.215534C25.0392 -0.0718446 24.4415 -0.0718446 24.3728 0.215534C24.3166 0.432109 24.2625 0.634106 24.2083 0.834022C24.0521 1.38171 23.8876 1.869 23.6939 2.30007C23.4919 2.75821 23.2629 3.15179 22.9859 3.49956C22.7152 3.84733 22.3987 4.14304 22.0301 4.40334C21.6573 4.66157 21.2304 4.87814 20.7348 5.06765C20.235 5.25923 19.6644 5.41541 19.0126 5.5591C18.392 5.69446 17.6944 5.809 16.9093 5.91937C16.624 5.95685 16.3304 5.99642 16.018 6.0339C15.5995 6.08388 15.5995 6.50037 16.018 6.55035L16.0097 6.54619H16.0139Z"
                            fill="white"
                        />
                    </svg>
                </Box>

                <Typography
                    variant="h6"
                    component={RouterLink}
                    to="/"
                    sx={{
                        textDecoration: 'none',
                        color: 'inherit',
                        flexGrow: 1,
                    }}
                >
                    Permanent Record
                </Typography>

                {email && (
                    <Typography
                        variant="caption"
                        color="inherit"
                        noWrap
                        sx={{
                            mx: 2,
                            display: { xs: 'none', sm: 'block' },
                            maxWidth: '120px',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            opacity: 0.8,
                        }}
                    >
                        {email}
                    </Typography>
                )}

                {isMobile ? (
                    <>
                        <IconButton
                            edge="end"
                            color="inherit"
                            aria-label="menu"
                            onClick={handleMenuOpen}
                        >
                            <MenuIcon />
                        </IconButton>

                        <Menu
                            anchorEl={anchorEl}
                            open={Boolean(anchorEl)}
                            onClose={handleMenuClose}
                        >
                            {email && (
                                <MenuItem
                                    disabled
                                    sx={{ justifyContent: 'center' }}
                                >
                                    <Typography
                                        variant="caption"
                                        noWrap
                                        sx={{
                                            maxWidth: '160px',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                        }}
                                    >
                                        {email}
                                    </Typography>
                                </MenuItem>
                            )}

                            {mobileMenuItems.map((item) => {
                                if (item.to) {
                                    // If “Create Record” is disabled, wrap it in a Tooltip + <span>

                                    if (
                                        item.to === '/record-editor' &&
                                        item.disabled
                                    ) {
                                        return (
                                            <Tooltip
                                                key="tooltip-mobile-create"
                                                title="You don’t have the ability to create records. Please contact Valar software team for access."
                                                placement="right"
                                                arrow
                                            >
                                                <span>
                                                    <MenuItem disabled>
                                                        Create Record
                                                    </MenuItem>
                                                </span>
                                            </Tooltip>
                                        );
                                    } else {
                                        return (
                                            <MenuItem
                                                key={item.label}
                                                component={RouterLink}
                                                to={item.to}
                                                onClick={handleMenuClose}
                                            >
                                                {item.label}
                                            </MenuItem>
                                        );
                                    }
                                } else {
                                    // e.g. Logout action
                                    return (
                                        <MenuItem
                                            key={item.label}
                                            onClick={item.action}
                                        >
                                            {item.label}
                                        </MenuItem>
                                    );
                                }
                            })}
                        </Menu>
                    </>
                ) : isLoggedIn() ? (
                    <>
                        <Button
                            component={RouterLink}
                            to="/dashboard"
                            variant="text"
                            color="inherit"
                            sx={{ ml: 2 }}
                        >
                            Dashboard
                        </Button>

                        {role === 'viewer' ? (
                            <Tooltip
                                title="You don’t have the ability to create records. Please contact Valar software team for access."
                                placement="bottom"
                                arrow
                            >
                                <span>
                                    <Button
                                        variant="contained"
                                        disabled
                                        sx={{
                                            ml: 2,
                                            // Override MUI’s disabled styling:
                                            '&.Mui-disabled': {
                                                backgroundColor:
                                                    theme.palette.background
                                                        .default,
                                                color: theme.palette.text
                                                    .primary,
                                                cursor: 'default',
                                                pointerEvents: 'none',
                                            },
                                        }}
                                    >
                                        Create Record
                                    </Button>
                                </span>
                            </Tooltip>
                        ) : (
                            <>
                                {!location.pathname.startsWith(
                                    '/record-editor'
                                ) && (
                                    <Button
                                        component={RouterLink}
                                        to="/record-editor"
                                        variant="contained"
                                        color="secondary"
                                        sx={{ ml: 2 }}
                                    >
                                        Create Record
                                    </Button>
                                )}
                                <Button
                                    component={RouterLink}
                                    to="/drafts"
                                    variant="outlined"
                                    color="secondary"
                                    sx={{ ml: 2 }}
                                    disabled={role !== 'editor'}
                                >
                                    View Drafts
                                </Button>
                            </>
                        )}

                        <Button
                            onClick={handleLogout}
                            variant="text"
                            color="inherit"
                            sx={{ ml: 2 }}
                        >
                            Logout
                        </Button>
                    </>
                ) : (
                    <>
                        <Button
                            component={RouterLink}
                            to="/login"
                            color="inherit"
                            sx={{ ml: 2 }}
                        >
                            Login
                        </Button>
                    </>
                )}
            </Toolbar>
        </AppBar>
    );
};

export default Navbar;
